<template>
  <BModal
    :model-value="modal"
    scrollable
    centered
    title="Cancel Extend"
    @update:modelValue="updateModal"
  >
    <template #header>
      <div class="d-flex align-items-center justify-content-between modal-title w-100">
        <h5 class="mb-0 text-danger">
          <i class="fas fa-undo me-2"></i> Cancel Extend Form
        </h5>
      </div>
      <BButton variant="link" class="close" @click="updateModal(false)">
        <i class="ion ion-android-close"></i>
      </BButton>
    </template>

    <BForm v-if="item" @submit.prevent="submitCancelExtend">
      <!-- Guest & Room Info -->
      <div class="card bg-light p-3 mb-3 border-0">
        <BRow class="mb-1">
          <BCol cols="5" class="text-secondary small">Guest Name:</BCol>
          <BCol cols="7" class="fw-bold small">{{ guestName }}</BCol>
        </BRow>
        <BRow class="mb-1">
          <BCol cols="5" class="text-secondary small">Room Number:</BCol>
          <BCol cols="7" class="fw-bold small">{{ roomCode }} ({{ roomCategoryName }})</BCol>
        </BRow>
        <BRow class="mb-1">
          <BCol cols="5" class="text-secondary small">Check-in Date:</BCol>
          <BCol cols="7" class="small">{{ displayCheckInDate }}</BCol>
        </BRow>
        <BRow class="mb-1">
          <BCol cols="5" class="text-secondary small">Current Check-out:</BCol>
          <BCol cols="7" class="fw-bold text-primary small">{{ displayCurrentCheckOutDate }}</BCol>
        </BRow>
        <BRow>
          <BCol cols="5" class="text-secondary small">Current Duration:</BCol>
          <BCol cols="7" class="small">{{ currentTotalDays }} night(s)</BCol>
        </BRow>
      </div>

      <!-- Form Inputs -->
      <BRow class="form-group mb-3">
        <BCol>
          <label class="form-label fw-semibold">Current Check-out Date</label>
          <BFormInput
            type="text"
            :model-value="displayCurrentCheckOutDate"
            disabled
          />
        </BCol>
      </BRow>

      <BRow class="form-group mb-3">
        <BCol>
          <label class="form-label fw-semibold">Select New Check-out Date (Rollback)</label>
          <BFormInput
            type="date"
            v-model="newCheckOutDate"
            :min="minCheckOutDate"
            :max="maxCheckOutDate"
            required
            @change="handleDateChange"
          />
          <small class="text-muted" style="font-size: 11px;">
            Pilih tanggal check-out sebelum perpanjangan (antara {{ minCheckOutDate }} s/d {{ maxCheckOutDate }}).
          </small>
        </BCol>
      </BRow>

      <BRow class="form-group mb-3">
        <BCol>
          <label class="form-label fw-semibold">Jumlah Malam yang Dibatalkan</label>
          <div class="input-group">
            <BFormInput
              type="number"
              v-model.number="cancelledNights"
              min="1"
              :max="maxCancelNights"
              required
              @input="handleNightsChange"
            />
            <span class="input-group-text">Malam</span>
          </div>
        </BCol>
      </BRow>

      <BRow class="form-group mb-3">
        <BCol>
          <label class="form-label fw-semibold">Room Rate / Night</label>
          <BFormInput
            type="number"
            v-model.number="roomPrice"
            min="0"
            required
          />
        </BCol>
      </BRow>

      <div class="form-group mb-3">
        <label class="form-label">Remark / Alasan Pembatalan (Opsional)</label>
        <BFormTextarea
          v-model="notes"
          rows="2"
          placeholder="Contoh: Tamu membatalkan extend menginap"
        />
      </div>

      <!-- Ringkasan Perubahan Biaya -->
      <div class="card bg-light p-3 mb-3 border">
        <div class="d-flex justify-content-between mb-1 small">
          <span>Cancelled Nights:</span>
          <strong>{{ cancelledNights }} night(s)</strong>
        </div>
        <div class="d-flex justify-content-between mb-1 small">
          <span>Rate / Night:</span>
          <strong>{{ formatIDR(roomPrice) }}</strong>
        </div>
        <div class="d-flex justify-content-between mb-1 small">
          <span class="text-danger fw-bold">Pengurangan Tagihan:</span>
          <strong class="text-danger">- {{ formatIDR(cancelledNights * roomPrice) }}</strong>
        </div>
        <div class="d-flex justify-content-between small">
          <span class="text-success fw-bold">New Check-out Date:</span>
          <strong class="text-success">{{ displayNewCheckOutDate }}</strong>
        </div>
      </div>

      <!-- Informasi Pembayaran Extend Terkait (Otomatis Dibatalkan) -->
      <div v-if="extendPayments.length > 0" class="alert alert-info py-2 px-3 mb-2 small">
        <div class="fw-bold mb-1">
          <i class="fas fa-info-circle me-1"></i> Pembayaran Extend Terdeteksi (Otomatis Dibatalkan):
        </div>
        <div
          v-for="pay in extendPayments"
          :key="pay.uuid"
          class="d-flex justify-content-between align-items-center bg-white p-2 mt-1 rounded border"
        >
          <div>
            <strong>{{ pay.description || 'Extend Payment' }}</strong>
            <div class="text-muted" style="font-size: 11px;">{{ pay.payment_date }}</div>
          </div>
          <span class="badge bg-danger">{{ formatIDR(pay.amount) }}</span>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 small mt-2">
        {{ errorMessage }}
      </div>
    </BForm>

    <template #footer>
      <div class="d-flex justify-content-end gap-2 w-100">
        <BButton variant="secondary" @click="updateModal(false)" :disabled="isSubmitting">
          Close
        </BButton>
        <BButton
          variant="danger"
          @click="submitCancelExtend"
          :disabled="isSubmitting || cancelledNights < 1 || cancelledNights > maxCancelNights"
        >
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fas fa-check me-1"></i>
          {{ isSubmitting ? 'Cancelling...' : 'Process Cancel Extend' }}
        </BButton>
      </div>
    </template>
  </BModal>

  <ToastResponse
    v-model="modalResponse.toast"
    :text="modalResponse.text"
    :success="modalResponse.success"
  />
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BForm,
  BFormInput,
  BFormTextarea,
  BButton,
} from "bootstrap-vue-next";
import { ref, computed, watch, inject } from "vue";
import ToastResponse from "../ToastResponse.vue";
import { formatIDR } from "@/utils/currency";
import { getFormattedDate } from "@/composable/getDateTimeFormatter";

export default {
  name: "CancelExtendForm",
  components: {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormTextarea,
    BButton,
    ToastResponse,
  },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modal", "addOrUpdate", "cancelSuccess"],
  setup(props, { emit }) {
    const repositories = inject("repositories");
    const inhouseRepository = repositories ? repositories.inhouse : null;

    const cancelledNights = ref(1);
    const newCheckOutDate = ref("");
    const roomPrice = ref(0);
    const notes = ref("");
    const isSubmitting = ref(false);
    const errorMessage = ref("");

    const modalResponse = ref({
      toast: false,
      text: "",
      success: false,
    });

    const resData = computed(() => {
      const raw = props.item?.item || props.item;
      return raw?.reservation || raw || {};
    });

    const guestName = computed(() => {
      return resData.value?.guest?.fullname || "-";
    });

    const roomCode = computed(() => {
      return resData.value?.room?.code || "-";
    });

    const roomCategoryName = computed(() => {
      return resData.value?.room?.room_category?.name || "-";
    });

    const rawCheckIn = computed(() => {
      const raw = props.item?.item || props.item;
      return raw?.check_in_date || resData.value?.start || null;
    });

    const rawCheckOut = computed(() => {
      const raw = props.item?.item || props.item;
      return raw?.check_out_date || resData.value?.end || null;
    });

    const currentTotalDays = computed(() => {
      return Number(resData.value?.days) || 1;
    });

    const maxCancelNights = computed(() => {
      return Math.max(1, currentTotalDays.value - 1);
    });

    const displayCheckInDate = computed(() => {
      return rawCheckIn.value ? getFormattedDate(rawCheckIn.value) : "-";
    });

    const displayCurrentCheckOutDate = computed(() => {
      return rawCheckOut.value ? getFormattedDate(rawCheckOut.value) : "-";
    });

    const displayNewCheckOutDate = computed(() => {
      return newCheckOutDate.value ? getFormattedDate(newCheckOutDate.value) : "-";
    });

    const minCheckOutDate = computed(() => {
      if (!rawCheckIn.value) return "";
      const d = new Date(rawCheckIn.value);
      d.setDate(d.getDate() + 1);
      return d.toISOString().split("T")[0];
    });

    const maxCheckOutDate = computed(() => {
      if (!rawCheckOut.value) return "";
      const d = new Date(rawCheckOut.value);
      d.setDate(d.getDate() - 1);
      return d.toISOString().split("T")[0];
    });

    const extendPayments = computed(() => {
      const payments = resData.value?.payment || resData.value?.payments || [];
      if (!Array.isArray(payments)) return [];
      return payments.filter((p) => {
        const desc = (p.description || "").toLowerCase();
        return (
          desc.includes("extend") ||
          desc.includes("perpanjang") ||
          desc.includes("extension")
        );
      });
    });

    const initFormData = () => {
      cancelledNights.value = 1;
      notes.value = "";
      errorMessage.value = "";

      const price =
        resData.value?.room_price ||
        resData.value?.room?.room_category?.low_season_price ||
        0;
      roomPrice.value = Number(price);

      // Hitung tanggal check-out baru default (check-out saat ini dikurangi 1 hari)
      if (rawCheckOut.value) {
        const d = new Date(rawCheckOut.value);
        d.setDate(d.getDate() - 1);
        newCheckOutDate.value = d.toISOString().split("T")[0];
      }
    };

    const handleDateChange = () => {
      if (!rawCheckOut.value || !newCheckOutDate.value) return;
      const end = new Date(rawCheckOut.value);
      const newEnd = new Date(newCheckOutDate.value);
      const diffTime = end.getTime() - newEnd.getTime();
      const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
      if (diffDays >= 1 && diffDays <= maxCancelNights.value) {
        cancelledNights.value = diffDays;
      }
    };

    const handleNightsChange = () => {
      if (!rawCheckOut.value) return;
      const nights = Number(cancelledNights.value) || 1;
      const d = new Date(rawCheckOut.value);
      d.setDate(d.getDate() - nights);
      newCheckOutDate.value = d.toISOString().split("T")[0];
    };

    watch(
      () => props.modal,
      (val) => {
        if (val) {
          initFormData();
        }
      }
    );

    const updateModal = (value) => {
      emit("update:modal", value);
    };

    const submitCancelExtend = async () => {
      if (cancelledNights.value < 1 || cancelledNights.value > maxCancelNights.value) {
        errorMessage.value = `Jumlah malam harus antara 1 dan ${maxCancelNights.value}.`;
        return;
      }

      const reservationUuid =
        resData.value?.uuid || props.item?.reservation_uuid;
      if (!reservationUuid) {
        errorMessage.value = "ID Reservasi tidak ditemukan.";
        return;
      }

      const payload = {
        days: Number(cancelledNights.value),
        room_price: Number(roomPrice.value),
        cancel_payment: true,
        notes: notes.value || null,
      };

      try {
        isSubmitting.value = true;
        errorMessage.value = "";

        const repo = inhouseRepository || (await import("@/repositories/inhouseRepository")).default;
        const res = await repo.cancelExtend(reservationUuid, payload);

        updateModal(false);
        emit("addOrUpdate");
        emit("cancelSuccess", {
          message: res.data?.message || "Perpanjangan berhasil dibatalkan.",
          details: res.data?.details,
        });

        modalResponse.value = {
          toast: true,
          text: res.data?.message || "Perpanjangan berhasil dibatalkan.",
          success: true,
        };
      } catch (err) {
        console.error("Error cancelling extend:", err);
        errorMessage.value =
          err.response?.data?.message ||
          "Gagal membatalkan perpanjangan. Silakan coba lagi.";
        modalResponse.value = {
          toast: true,
          text: errorMessage.value,
          success: false,
        };
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      cancelledNights,
      newCheckOutDate,
      roomPrice,
      notes,
      isSubmitting,
      errorMessage,
      modalResponse,
      guestName,
      roomCode,
      roomCategoryName,
      currentTotalDays,
      maxCancelNights,
      displayCheckInDate,
      displayCurrentCheckOutDate,
      displayNewCheckOutDate,
      minCheckOutDate,
      maxCheckOutDate,
      extendPayments,
      updateModal,
      handleDateChange,
      handleNightsChange,
      submitCancelExtend,
      formatIDR,
    };
  },
};
</script>

<style scoped>
.small {
  font-size: 13px;
}
</style>
